/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { CONNECTION_STATUS_NAME } from 'app-constants'
import {
  contactService,
  contractService,
  financialService,
  productService,
  wifiService,
} from 'services'
import { formatCurrency, getTvLinks, toISO } from 'utils'

const initialState = {
  selected: null,
  detail: null,
  motv: null,
  internet: null,
  telephony: null,
  unlockDate: null,
  connectionStatus: null,
  connectionStatusDescription: '',
  isMassiveCondition: false,
  isSignalWeak: false,
  activationLinks: [],
  objection: null,
  isLoading: false,
  fidelity: null,
}

const { updateWifi, getInternet, getConnectionStatusRequest } = wifiService()
const { getTelephony, togglePaidCall } = contactService()
const { unlockContracts, getContractDetails } = contractService()
const { getObjections } = financialService()
const { getFidelityUpgradePlan, activateHero } = productService()

export const fetchContractDetails = createAsyncThunk('contract/fetchContractDetails', async id => {
  const { data } = await getContractDetails(id)

  const payload = {
    ...data,
    formattedAdditionalTvPointsTotalPrice: formatCurrency(data.additionalTvPointsTotalPrice),
    formattedTotalPrice: formatCurrency(data.totalPrice),
    contractAdditionals: data.contractAdditionals.map(item => ({
      ...item,
      formattedItemPrice: formatCurrency(item.itemPrice),
    })),
    isAllowedCreditCardPayment: data.allowCreditCardPayment,
  }

  return payload
})

export const fetchInternet = createAsyncThunk('contract/fetchInternet', async id => {
  const { data } = await getInternet(id)

  return data
})

export const fetchTelephony = createAsyncThunk('contract/fetchTelephony', async id => {
  const { data } = await getTelephony(id)

  return data
})

export const updatePaidCall = createAsyncThunk(
  'contract/updatePaidCall',
  async ({ idContract, phoneId, collectCall }) => {
    await togglePaidCall(idContract, { phoneId, collectCall })

    return collectCall
  }
)

export const updateInternet = createAsyncThunk(
  'contract/updateInternet',
  async ({ idContract, ssid, password }, { getState }) => {
    const body = { ...getState().contract.internet, ssid, wifiPassword: password }

    await updateWifi(idContract, body)

    return { ssid, password }
  }
)

export const unlockAccount = createAsyncThunk('contract/unlockAccount', async ({ idContract }) => {
  await unlockContracts(idContract)

  return toISO(new Date())
})

export const activateContractHero = createAsyncThunk(
  'contract/activateContractHero',
  async (contractId, { rejectWithValue }) => {
    try {
      const { data } = await activateHero(contractId)

      return data.heroLicence
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchConnectionStatus = createAsyncThunk(
  'contract/connectionStatus',
  async contractId => {
    const { data } = await getConnectionStatusRequest({ contractId })

    return data
  }
)

export const fetchTvPlansActivationLink = createAsyncThunk(
  'contract/activationLinks',
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        detail,
        selected: { id: contractId },
      } = getState().contract
      const results = await getTvLinks(contractId, detail?.contractAdditionals)

      return results
    } catch (error) {
      console.error(error)
      return rejectWithValue()
    }
  }
)

export const fetchObjections = createAsyncThunk('contract/objections', async contractId => {
  const { data } = await getObjections({ contractId })

  return data
})

export const fetchFidelityUpgradePlan = createAsyncThunk(
  'contract/upgrade-plan',
  async contractId => {
    const { data } = await getFidelityUpgradePlan(contractId)

    return data
  }
)

const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    selectContract: (state, action) => {
      state.selected = action.payload
      state.connectionStatus = null
    },
    setInternetInfo: (state, action) => {
      state.internet = action.payload
    },
    updatePlanUpgrade: state => {
      state.fidelity = {
        isEligible: false,
        isLoading: false,
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchContractDetails.fulfilled, (state, action) => {
        state.detail = action.payload
        state.telephony = {}
      })
      .addCase(fetchContractDetails.rejected, state => {
        state.detail = {}
        state.telephony = {}
      })
      .addCase(fetchInternet.fulfilled, (state, action) => {
        state.internet = action.payload
      })
      .addCase(fetchInternet.rejected, state => {
        state.internet = { error: true }
      })
      .addCase(fetchTelephony.fulfilled, (state, action) => {
        const { id } = action.payload
        state.telephony = {
          ...action.payload,
          id: Number(id),
        }
      })
      .addCase(fetchTelephony.rejected, state => {
        state.telephony = {}
      })
      .addCase(updatePaidCall.fulfilled, (state, action) => {
        state.telephony.collectCall = action.payload
      })
      .addCase(updatePaidCall.rejected, state => {
        state.telephony.collectCall = !state.telephony.collectCall
      })
      .addCase(updateInternet.fulfilled, (state, action) => {
        state.internet.ssid = action.payload.ssid
        state.internet.password = action.payload.password
      })
      .addCase(unlockAccount.fulfilled, (state, action) => {
        state.unlockDate = action.payload
      })
      .addCase(unlockAccount.rejected, state => {
        state.unlockDate = null
      })
      .addCase(activateContractHero.fulfilled, state => {
        state.selected.hasHeroActive = true
      })
      .addCase(fetchConnectionStatus.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchConnectionStatus.fulfilled, (state, action) => {
        state.connectionStatus = action.payload.status
        state.connectionStatusDescription = action.payload.description
        state.isMassiveCondition = action.payload.isMassiveCondition
        state.isSignalWeak = action.payload.isSignalWeak
        state.isLoading = false
      })
      .addCase(fetchConnectionStatus.rejected, state => {
        state.connectionStatus = CONNECTION_STATUS_NAME.UNKNOWN
        state.isLoading = false
      })
      .addCase(fetchTvPlansActivationLink.fulfilled, (state, action) => {
        state.activationLinks = action.payload
      })
      .addCase(fetchTvPlansActivationLink.rejected, state => {
        state.activationLinks = []
      })
      .addCase(fetchObjections.fulfilled, (state, action) => {
        const hasObjection = !!action.payload.ticketId
        if (hasObjection) state.objection = action.payload
        else state.objection = null
      })
      .addCase(fetchObjections.rejected, state => {
        state.objection = null
      })
      .addCase(fetchFidelityUpgradePlan.fulfilled, (state, action) => {
        state.fidelity = {
          ...action.payload,
          isLoading: false,
        }
      })
      .addCase(fetchFidelityUpgradePlan.pending, state => {
        state.fidelity = {
          isEligible: false,
          isLoading: true,
        }
      })
      .addCase(fetchFidelityUpgradePlan.rejected, state => {
        state.fidelity = {
          isEligible: false,
          isLoading: false,
        }
      })
  },
})

export const { selectContract, setInternetInfo, updatePlanUpgrade } = contractSlice.actions

export default contractSlice.reducer
